/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { CustomLink } from "../../custom-link";
import get from "lodash/get";
import { AccessType } from "@quintype/components";
import { accessCheck } from "../../../../isomorphic/utils";
import "./request-subscription.m.css";
import { SvgHandler } from "../../svg-handler/svg-handler";
import loaderIcon from "../../../../assets/icons/loading.svg";

class RequestSubscriptionBlock extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showForm: true,
      loader: false,
      successCallout: false,
      errorCallout: false
    };
  }

  hideForm = event => {
    event.stopPropagation();
    event.preventDefault();
    this.setState({ showForm: !this.state.showForm });
  };

  openLogin = event => {
    event.preventDefault();
    event.stopPropagation();
    this.props.openLoginTemplate();
  };

  handleSubscriptionClick = async () => {
    if (!this.props.member) {
      this.props.openLoginTemplate();
      return false;
    }
    this.setState({ loader: true });
    const accessPlan = get(this.props, ["accessPlan"], {});
    const { story } = this.props;
    try {
      const { subscription } = await this.props.initRazorPayPayment(
        accessPlan,
        "dynamic_asset",
        story.id,
        story.headline,
        story.slug,
        story['["hero-image-s3-key"]']
      );
      if (subscription) {
        this.setState({ successCallout: true, loader: false });
        setTimeout(() => {
          this.setState({ successCallout: false, loader: false });
          this.props.navigateTo(`/${story.slug}`);
        }, 1000);
        return true;
      }
    } catch {
      this.setState({ errorCallout: true, loader: false, successCallout: false });
      console.log("user closed the payment");
    }
  };

  getSubscriptionRequestBlock = () => {
    const { config } = this.props || {};
    const PbAccessTypeConfig = get(config, ["pagebuilder-config", "general", "accesstypeIntegration"]);
    const publisherTitle = get(config, ["publisher-settings", "title"], "Ahead");
    const enablePayPerAsset = get(PbAccessTypeConfig, ["enablePayPerAsset"]);
    const accessPlan = get(this.props, ["accessPlan", "price_cents"], "") / 100;
    const currencyType = get(this.props, ["accessPlan", "price_currency"]);

    return (
      <>
        <div styleName="subs-headline">Subscribe now to get unlimited access</div>
        <p styleName="subs-desc">
          {`We're glad you're enjoying ${publisherTitle}. Login and subscribe to continue reading this story.`}
        </p>
        <div styleName="btn-wrapper">
          <div styleName="primary-button">
            <CustomLink href={"/subscription"}>Subscribe</CustomLink>
          </div>
          {enablePayPerAsset && (
            <span styleName="secondary-button" onClick={this.handleSubscriptionClick}>
              Pay {currencyType} {accessPlan} to read
            </span>
          )}
        </div>
        {!this.props.member && (
          <span styleName="info-text">
            Already a user? <a onClick={this.openLogin}>Login</a>
          </span>
        )}
      </>
    );
  };

  render() {
    return (
      <>
        {this.state.showForm && (
          <div className="element-wrapper">
            <div styleName="subs-wrapper">
              {!this.state.successCallout &&
                (!this.state.loader && !this.state.errorCallout && <>{this.getSubscriptionRequestBlock()}</>)}

              <div styleName="block-wrapper processing-block">
                {this.state.loader ? (
                  <div styleName="loader">
                    <p>Payment is processing</p>
                    <SvgHandler width="50" height="50" clazzName="callout-loader" xlinkHref={loaderIcon} />
                  </div>
                ) : null}
              </div>
              <div styleName="block-wrapper">{this.state.successCallout ? <ThankyouBlock /> : null}</div>
              <div styleName="block-wrapper">{this.state.errorCallout ? <ErrorBlock /> : null}</div>
            </div>
          </div>
        )}
      </>
    );
  }
}

RequestSubscriptionBlock.propTypes = {
  member: PropTypes.object,
  openLoginTemplate: PropTypes.func,
  initAccessType: PropTypes.func,
  initRazorPayPayment: PropTypes.func,
  getSubscriptionForUser: PropTypes.func,
  navigateTo: PropTypes.func,
  story: PropTypes.object
};

function RequestSubscriptionBase(props) {
  const email = get(props, ["member", "email"]);
  const phone = get(props, ["member", "metadata", "phone-number"], "1234");
  const themeAttributes = get(props, ["config", "theme-attributes"]);
  const PbAccessTypeConfig = get(props, ["config", "pagebuilder-config", "general", "accesstypeIntegration"]);
  const accessTypeKey = get(PbAccessTypeConfig, ["accesstypeKey"], get(themeAttributes, ["accesstypeKey"]));
  const isStaging =
    get(PbAccessTypeConfig, ["accesstypeMode"]) === "staging" ||
    get(themeAttributes, ["accesstypeEnvironment"]) === "staging";

  const enableAccesstype = get(
    PbAccessTypeConfig,
    ["enableAccesstype"],
    get(themeAttributes, ["enableAccesstype"], false)
  );

  const storyId = get(props, ["story", "id"]);

  const accessTypeProdHost = get(PbAccessTypeConfig, ["accessTypeProdHost"]);

  const accessTypeStgHost = get(PbAccessTypeConfig, ["accessTypeStgHost"]);

  return (
    <AccessType
      enableAccesstype={accessCheck(enableAccesstype)}
      isStaging={isStaging}
      accessTypeKey={accessTypeKey}
      email={email}
      phone={phone}
      storyId={storyId}
      prodHost={accessTypeProdHost}
      stagingHost={accessTypeStgHost}
    >
      {({ initRazorPayPayment, getSubscriptionForUser }) => (
        <RequestSubscriptionBlock
          initRazorPayPayment={initRazorPayPayment}
          getSubscriptionForUser={getSubscriptionForUser}
          {...props}
        />
      )}
    </AccessType>
  );
}

export const ThankyouBlock = () => {
  return (
    <div>
      <div styleName="subs-headline success-msg-color">Thank you !</div>
      <p styleName="subs-desc">Your payment was successful. Now you can continue reading the article</p>
      <p>This notification will be closed in 1 seconds…</p>
    </div>
  );
};

export const ErrorBlock = () => {
  const tryAgainBtn = () => {
    this.setState({ errorCallout: false });
  };

  return (
    <div>
      <div styleName="subs-headline error-msg-color">Oops! Something went wrong.</div>
      <p styleName="subs-desc">Payment was not successful</p>
      <button styleName="try-again-btn" onClick={tryAgainBtn}>
        Try again
      </button>
    </div>
  );
};

const mapStateToProps = state => ({
  member: get(state, ["member"], null),
  config: get(state, ["qt", "config"], {}),
  accessPlan: get(state, ["assetPlans", 0], {})
});

const mapDispatchToProps = dispatch => ({
  openLoginTemplate: () => dispatch({ type: "OPEN_LOGIN_TEMPLATE" }),
  navigateTo: url => global.app.navigateToPage(dispatch, url)
});

export const RequestSubscription = connect(
  mapStateToProps,
  mapDispatchToProps
)(RequestSubscriptionBase);
